/**
 * How the tables in a report are set.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add an option, add a row to the table it belongs to and nothing else.**
 *
 * As with the measurements and the colours, each table below is the single
 * definition of its setting: the type and the list of ids follow from it, so a
 * new density is one line here plus its two names in the message catalogue.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * A financial statement is mostly tables, so how they are set is most of how
 * the report looks. The choices are the ones that actually change how a page
 * of figures reads, and every one of them is presentational: nothing here
 * touches what a cell says. A setting that rewrote a figure would be a setting
 * that could put a wrong number in a signed document.
 *
 * Pure. Measurements and names, no rendering.
 */

/**
 * Which lines a table is drawn with.
 *
 * The default is horizontal, which is how a printed statement is ruled: the
 * eye follows a row across, and a vertical line between two columns of figures
 * is a line it has to cross rather than one that helps.
 */
export const TABLE_RULES = {
  /** Every line drawn, as a working paper is. */
  all: { outer: '1px', inner: '1px', row: '1px' },
  /** Across only, which is what a printed statement uses. */
  horizontal: { outer: '0', inner: '0', row: '1px' },
  /** The header underlined and the total ruled, and nothing else. */
  minimal: { outer: '0', inner: '0', row: '0' },
} as const

/** How much air a row of figures gets. */
export const TABLE_DENSITIES = {
  compact: { padding: '1mm 2mm', leading: '1.25' },
  normal: { padding: '1.6mm 2.5mm', leading: '1.4' },
  roomy: { padding: '2.4mm 3mm', leading: '1.5' },
} as const

/**
 * The band behind every other row, where rows are banded: faint, because it is
 * there to lead the eye across a wide table, not to colour it.
 */
export const TABLE_ZEBRA = 'rgba(16, 32, 51, 0.028)'

/**
 * How a table's head row is drawn.
 *
 * Underlined is the house default: a rule in the accent under the column
 * names. Tinted lays a pale wash of the colour behind them, filled sets them
 * in the ink that reads on the colour, plain keeps them bold over a quiet
 * rule. Each is a wash of the colour behind the names, a rule under them and
 * how much of the colour that rule takes, which is what the page's one rule
 * for a head cell draws (see `builder.css`).
 */
export const TABLE_HEADS = {
  underline: { wash: '0%', line: '1.2px', lineInk: '100%' },
  tinted: { wash: '11%', line: '1.2px', lineInk: '100%' },
  filled: { wash: '100%', line: '1.2px', lineInk: '100%' },
  plain: { wash: '0%', line: '1px', lineInk: '0%' },
} as const

export type TableRule = keyof typeof TABLE_RULES
export type TableDensity = keyof typeof TABLE_DENSITIES
export type TableHead = keyof typeof TABLE_HEADS

export const TABLE_RULE_IDS = Object.keys(TABLE_RULES) as readonly TableRule[]
export const TABLE_DENSITY_IDS = Object.keys(TABLE_DENSITIES) as readonly TableDensity[]
export const TABLE_HEAD_IDS = Object.keys(TABLE_HEADS) as readonly TableHead[]
